import { useAuth } from "@clerk/nextjs";
import { ButtonVariant, CoButton } from "components/CoButton";
import { NumberedSteps } from "components/NumberedSteps/NumberedSteps";
import path from "constants/path";
import AskPaperPdfLoading from "features/AskPaperDrawer/components/AskPaperPdfLoading";
import useAskPaperDrawer from "features/AskPaperDrawer/hooks/useAskPaperDrawer";
import { useResearchPaperUpload } from "features/ResearchHub/hooks/useResearchPaperUpload";
import { MixpanelActionSource } from "helpers/services/mixpanel/events";
import useLabels from "hooks/useLabels";
import Link from "next/link";
import { useRouter } from "next/router";
import { useState } from "react";
import { toast } from "react-toastify";

type AskPaperUploadNewPaperGuideProps = {
  fullTextURL?: string;
  onClose: () => void;
};

function AskPaperUploadNewPaperGuide({
  onClose,
}: AskPaperUploadNewPaperGuideProps) {
  const router = useRouter();
  const [askPaperLabels] = useLabels("ask-paper-upload-new-paper-guide");
  const { isSignedIn, isLoaded } = useAuth();

  const { fullTextURL, paperId, title, closeAskPaperDrawer } =
    useAskPaperDrawer();

  // The reason we're using a state variable here is because the PDF will load and then
  // the page will reload. If we don't have a state variable, then it will flash back to
  // the original state before the PDF loaded (and then the page would reload again).
  const [isUploading, setIsUploading] = useState(false);

  const { triggerFileSelection } = useResearchPaperUpload({
    onUploadTriggered: () => {
      setIsUploading(true);
      toast.info(askPaperLabels["uploading-paper"]);
    },
    onSuccess: (pathname?: string, fullTextURL?: string) => {
      toast.success(askPaperLabels["toast-success"]);

      setTimeout(() => {
        const baseUrl = router.basePath;
        const path = pathname;
        const params = new URLSearchParams({
          openAskPaperDrawer: "true",
          paperId: paperId as string,
          title: title as string,
          fullTextURL: fullTextURL as string,
        });

        window.location.href = `${baseUrl}${path}?${params.toString()}`;
      }, 1000);
    },
    onError: (errorMessage?: string) => {
      toast.error(errorMessage || askPaperLabels["toast-error"]);
      setIsUploading(false);
    },
    actionSource: MixpanelActionSource.AskPaper,
    force_hash_paper_id: paperId,
  });

  const steps = [
    {
      title: askPaperLabels["check-if-you-have-access"],
      description: (
        <>
          {askPaperLabels["your-uploaded-papers-are-always-private"]}
          <Link
            className="underline"
            href={path.RESEARCH_HUB_UPLOADED}
            onClick={() => {
              closeAskPaperDrawer();
            }}
          >
            {askPaperLabels["research-hub"]}
          </Link>
        </>
      ),
      content: (
        <CoButton
          variant={ButtonVariant.Tertiary}
          label="Full text"
          icon="icon-external-link"
          size="sm"
          disabled={!fullTextURL}
          href={fullTextURL || ""}
          target="_blank"
        />
      ),
    },
    {
      title: askPaperLabels["upload-full-text-here"],
      description: askPaperLabels["upload-full-text-here-description"],
      content: (
        <CoButton
          variant={ButtonVariant.Secondary}
          label={
            isUploading
              ? askPaperLabels["uploading-paper"]
              : askPaperLabels["upload-paper"]
          }
          icon="icon-upload"
          size="sm"
          onClick={triggerFileSelection}
          disabled={isUploading || !isSignedIn || !isLoaded}
        />
      ),
    },
  ];

  if (isUploading) {
    return <AskPaperPdfLoading />;
  }

  return (
    <div className="flex flex-col h-full overflow-y-auto">
      <h2 className="text-lg font-medium mb-3">
        {askPaperLabels["full-text-is-not-available"]}
      </h2>
      <p className="text-body-sm-normal mb-8">
        {askPaperLabels["full-text-is-not-available-description"]}
      </p>

      <NumberedSteps steps={steps} />
    </div>
  );
}

export default AskPaperUploadNewPaperGuide;
